import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { getService } from "@/data/services";
import { ServiceIcon } from "@/components/icons";
import Reveal from "@/components/motion/Reveal";

// Asymmetrical bento layout (Tetris mode)
const LAYOUT = [
  { slug: "chiptuning-cloppenburg", span: "md:col-span-3", big: true },
  { slug: "schluesseldienst-cloppenburg", span: "md:col-span-3", big: true },
  { slug: "softwareoptimierung-cloppenburg", span: "md:col-span-2" },
  { slug: "steuergeraet-reparatur", span: "md:col-span-2" },
  { slug: "auto-notoeffnung", span: "md:col-span-2" },
  { slug: "kfz-schluessel-nachmachen", span: "md:col-span-2" },
  { slug: "wegfahrsperre-deaktivieren", span: "md:col-span-2" },
  { slug: "kfz-codierungen", span: "md:col-span-2" },
];

function Card({ conf, index }) {
  const s = getService(conf.slug);
  const emergency = s.emergency;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className={conf.span}
    >
      <Link
        to={`/${s.slug}`}
        data-testid={`service-card-${s.slug}`}
        className={`group relative flex h-full min-h-[220px] flex-col justify-between overflow-hidden border p-7 transition-all duration-300 ${
          emergency
            ? "border-[#FF3B30]/30 bg-[#140505] hover:border-[#FF3B30]"
            : "border-white/10 bg-[#0d0d0d] hover:border-volt"
        } hover:-translate-y-1`}
      >
        {conf.big && (
          <div className="absolute inset-0 -z-0 opacity-25 transition-opacity duration-500 group-hover:opacity-40">
            <img src={s.image} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/70 to-[#0d0d0d]/30" />
          </div>
        )}
        <div className="relative flex items-start justify-between">
          <div className={`flex h-12 w-12 items-center justify-center border ${emergency ? "border-[#FF3B30]/40 text-[#FF3B30]" : "border-volt/40 text-volt"}`}>
            <ServiceIcon name={s.icon} className="h-6 w-6" />
          </div>
          <ArrowUpRight className={`h-6 w-6 text-neutral-600 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 ${emergency ? "group-hover:text-[#FF3B30]" : "group-hover:text-volt"}`} />
        </div>
        <div className="relative mt-6">
          <p className={`font-body text-[10px] font-bold uppercase tracking-[0.2em] ${emergency ? "text-[#FF3B30]" : "text-volt"}`}>
            {s.eyebrow}
          </p>
          <h3 className={`mt-1 font-heading font-extrabold uppercase leading-none text-white ${conf.big ? "text-3xl sm:text-4xl" : "text-2xl"}`}>
            {s.nav}
          </h3>
          {conf.big && (
            <p className="mt-3 max-w-md font-body text-sm text-neutral-400 line-clamp-2">
              {s.intro}
            </p>
          )}
        </div>
      </Link>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="leistungen" className="relative bg-[#050505] py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal className="mb-14 max-w-2xl">
          <p className="mb-4 flex items-center gap-3 font-body text-[11px] font-bold uppercase tracking-[0.3em] text-volt">
            <span className="h-px w-10 bg-volt" /> Unsere Leistungen
          </p>
          <h2 className="font-heading text-4xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-6xl">
            Alles für <span className="text-volt">Motor & Schlüssel</span>
          </h2>
          <p className="mt-5 font-body text-base text-neutral-400">
            Von der Leistungssteigerung bis zur Notöffnung – jede Leistung mit eigener,
            detaillierter Seite für deinen Bedarf im Raum Cloppenburg.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
          {LAYOUT.map((conf, i) => (
            <Card key={conf.slug} conf={conf} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
